import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Play, RotateCcw, FlaskConical, DollarSign, TrendingUp, AlertTriangle, CheckCircle, Rocket, History, Users, ChevronDown, CalendarClock, Building2, Sparkles, ScrollText, Copy, Mail } from 'lucide-react';
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const fmt$ = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : `$${Math.round(v || 0).toLocaleString()}`;

const VOLUME_FIELDS = [
  { key: 'nb_per_day', label: 'NB Quotes / day' },
  { key: 'renewals_per_day', label: 'Renewals / day' },
  { key: 'claims_per_day', label: 'Claims / day' },
  { key: 'endorsements_per_day', label: 'Endorsements / day' },
  { key: 'cancellations_per_day', label: 'Cancellations / day' },
  { key: 'daily_budget_usd', label: 'LLM budget $ / day' },
];

export default function SimulationPage() {
  const api = useApi();
  const [config, setConfig] = useState(null);
  const [runs, setRuns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [growth, setGrowth] = useState(null);
  const [growthTarget, setGrowthTarget] = useState('');
  const [seed, setSeed] = useState(null);
  const [seedLr, setSeedLr] = useState('52');
  const [growthBook, setGrowthBook] = useState('sim');
  const [history, setHistory] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [selBrokers, setSelBrokers] = useState([]);
  const [showBrokers, setShowBrokers] = useState(false);
  const [wave, setWave] = useState(null);
  const [presets, setPresets] = useState([]);
  const [firms, setFirms] = useState([]);
  const [showFirms, setShowFirms] = useState(false);
  const [driveRate, setDriveRate] = useState('85');
  const [demo, setDemo] = useState(null);
  const [retention, setRetention] = useState(null);
  const [showRetention, setShowRetention] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, r, s] = await Promise.all([
        api.get('/simulation/config'), api.get('/simulation/runs'), api.get('/simulation/summary'),
      ]);
      setConfig(c.data); setRuns(r.data); setSummary(s.data);
    } catch (err) { console.error('API error:', err?.message); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const loadGrowth = useCallback(async () => {
    try {
      const [{ data }, s, h, w, p, dm] = await Promise.all([
        api.get('/simulation/grow/status'), api.get('/simulation/grow/seed-claims/status'),
        api.get('/simulation/grow/history'), api.get('/simulation/grow/renewal-wave'),
        api.get('/simulation/grow/presets'), api.get('/simulation/grow/demo-book/status'),
      ]);
      setGrowth(data); setSeed(s.data); setHistory(h.data); setWave(w.data); setPresets(p.data); setDemo(dm.data);
      return { growth: data, seed: s.data, demo: dm.data };
    } catch (err) { console.error('API error:', err?.message); return null; }
  }, [api]);
  useEffect(() => { loadGrowth(); }, [loadGrowth]);
  useEffect(() => {
    if (growthBook !== 'live' || brokers.length > 0) return;
    api.get('/simulation/grow/brokers').then(({ data }) => setBrokers(data)).catch(() => {});
  }, [growthBook, brokers.length, api]);
  useEffect(() => {
    if (growth?.status !== 'running' && seed?.status !== 'running' && demo?.status !== 'running') return;
    const t = setInterval(async () => {
      const r = await loadGrowth();
      if (r && r.growth?.status !== 'running' && r.seed?.status !== 'running' && r.demo?.status !== 'running') load();
    }, 3000);
    return () => clearInterval(t);
  }, [growth?.status, seed?.status, demo?.status, loadGrowth, load]);

  const startGrowth = async () => {
    const target = Number(growthTarget);
    if (!target) { toast.error('Enter a target GWP'); return; }
    if (growthBook === 'live' && !window.confirm(`Grow the LIVE book to $${target.toLocaleString()}? These policies are NOT purged by Reset Simulation.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post('/simulation/grow', { target_gwp: target, book: growthBook, broker_ids: growthBook === 'live' && selBrokers.length > 0 ? selBrokers : undefined });
      toast.success(data.message);
      setTimeout(loadGrowth, 2000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Growth start failed'); }
    setBusy(false);
  };

  const applyPreset = (p) => {
    setGrowthBook(p.book || 'sim');
    if (p.target_gwp) setGrowthTarget(String(p.target_gwp));
    setSelBrokers(p.broker_ids || []);
    setSeedLr(String(Math.round((p.target_lr || 0.52) * 100)));
    toast.success(`Preset "${p.name}" loaded — review and hit Grow Book`);
  };

  const savePreset = async () => {
    const name = window.prompt('Preset name (target, book, broker mix and LR will be saved):');
    if (!name) return;
    try {
      const { data } = await api.post('/simulation/grow/presets', {
        name, book: growthBook, target_gwp: Number(growthTarget) || null,
        broker_ids: growthBook === 'live' ? selBrokers : [], target_lr: Number(seedLr) / 100,
      });
      setPresets(prev => [...prev, data]);
      toast.success(`Preset "${data.name}" saved`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const deletePreset = async (p) => {
    try {
      await api.delete(`/simulation/grow/presets/${p.preset_id}`);
      setPresets(prev => prev.filter(x => x.preset_id !== p.preset_id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const prepareWave = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/simulation/grow/renewal-wave/prepare');
      toast.success(`ARIA prepared ${data.prepared} renewal offers (${data.held_for_review} held for review, ${data.already_prepared} already existed)`);
      await loadGrowth();
    } catch (err) { toast.error(err.response?.data?.detail || 'Prepare failed'); }
    setBusy(false);
  };

  const buildDemoBook = async () => {
    const target = Number(growthTarget);
    if (!target) { toast.error('Enter a target GWP'); return; }
    if (!window.confirm(`Build a full demo book? This will grow the ${growthBook} book to $${target.toLocaleString()}, seed a claims history at ${seedLr}% LR, prepare renewal offers and drive broker responses at ${driveRate}% acceptance.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post('/simulation/grow/demo-book', {
        target_gwp: target, book: growthBook, target_lr: Number(seedLr) / 100,
        accept_rate: Number(driveRate) / 100,
        broker_ids: growthBook === 'live' && selBrokers.length > 0 ? selBrokers : undefined,
      });
      toast.success(data.message);
      setTimeout(loadGrowth, 2000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Demo book start failed'); }
    setBusy(false);
  };

  const driveWave = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/simulation/grow/renewal-wave/drive', { accept_rate: Number(driveRate) / 100 });
      toast.success(`ARIA drove ${data.responded} broker responses — ${data.accepted} accepted (${fmt$(data.accepted_gwp)} renewed), ${data.declined} declined`);
      await loadGrowth();
    } catch (err) { toast.error(err.response?.data?.detail || 'Drive failed'); }
    setBusy(false);
  };

  const generateScript = async () => {
    setBusy(true);
    try {
      await api.post('/simulation/grow/demo-book/script');
      toast.success('Demo script generated');
      await loadGrowth();
    } catch (err) { toast.error(err.response?.data?.detail || 'Script generation failed'); }
    setBusy(false);
  };

  const copyScript = () => {
    const sc = demo?.script;
    if (!sc) return;
    const text = `${sc.headline}\n\nTALKING POINTS\n${sc.talking_points.map((t, i) => `${i + 1}. ${t}`).join('\n')}\n\nDEMO FLOW\n${(sc.demo_flow || []).map((t, i) => `${i + 1}. ${t}`).join('\n')}`;
    navigator.clipboard.writeText(text).then(() => toast.success('Script copied to clipboard'));
  };

  const undoRun = async (r) => {
    if (!window.confirm(`Undo this ${r.type === 'growth' ? 'growth' : 'claims seed'} run? ${r.type === 'growth' ? 'All policies, claims and submissions it created will be removed.' : 'Seeded claims will be removed and policy dates restored.'}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post('/simulation/grow/undo', { run_id: r.run_id, type: r.type });
      const parts = Object.entries(data).filter(([k]) => k.endsWith('_deleted') || k.endsWith('_restored')).map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`);
      toast.success(`Run undone — ${parts.join(', ')}`);
      await Promise.all([loadGrowth(), load()]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Undo failed'); }
    setBusy(false);
  };

  const startSeed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/simulation/grow/seed-claims', { target_lr: Number(seedLr) / 100 });
      toast.success(data.message);
      setTimeout(loadGrowth, 2000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Claims seed failed'); }
    setBusy(false);
  };

  const saveConfig = async (changes) => {
    try {
      const { data } = await api.put('/simulation/config', changes);
      setConfig(data);
      toast.success('Simulation config saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/simulation/run');
      toast.success(data.message);
      setTimeout(load, 15000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Run failed'); }
    setBusy(false);
  };

  const resetSim = async () => {
    if (!window.confirm('Reset ALL simulation records and revert every auto-applied calibration? This cannot be undone.')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/simulation/reset');
      toast.success(`Reset complete — ${Object.values(data.deleted).reduce((a, b) => a + b, 0)} records purged, ${data.calibrations_reverted.length} calibrations reverted`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
    setBusy(false);
  };

  if (!config) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;

  const latest = runs[0];
  return (
    <div className="space-y-5" data-testid="simulation-page">
      {/* Header / controls */}
      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FlaskConical className="w-5 h-5" style={{ color: C.teal }} />
            <div>
              <h2 className="text-sm font-bold" style={{ color: C.navy }}>Daily Simulation Engine</h2>
              <p className="text-xs" style={{ color: C.slate }}>Drill transactions flow through real workflows, tagged simulation — training the agents daily</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: C.slate }}>Enabled</span>
              <Switch checked={config.enabled} onCheckedChange={(v) => saveConfig({ enabled: v })} data-testid="sim-enabled-switch" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: C.slate }}>Auto-calibration</span>
              <Switch checked={config.auto_calibration} onCheckedChange={(v) => saveConfig({ auto_calibration: v })} data-testid="sim-calibration-switch" />
            </div>
            <button onClick={runNow} disabled={busy} data-testid="sim-run-now-button"
              className="flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs font-semibold text-white" style={{ background: C.blue }}>
              <Play className="w-3.5 h-3.5" /> Run Now
            </button>
            <button onClick={resetSim} disabled={busy} data-testid="sim-reset-button"
              className="flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs font-semibold" style={{ color: C.red, border: `1px solid ${C.red}40` }}>
              <RotateCcw className="w-3.5 h-3.5" /> Reset Simulation
            </button>
          </div>
        </div>
        {/* Volumes */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-3">
          {VOLUME_FIELDS.map(f => (
            <div key={f.key}>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{f.label}</label>
              <input type="number" min="0" step={f.key === 'daily_budget_usd' ? '0.1' : '1'} defaultValue={config[f.key]}
                onBlur={(e) => { const v = Number(e.target.value); if (v !== config[f.key]) saveConfig({ [f.key]: v }); }}
                className="mt-1 w-full px-2 py-1.5 text-xs rounded-sm border focus:outline-none focus:ring-2 focus:ring-[#1A4C7C]"
                style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} data-testid={`sim-${f.key}-input`} />
            </div>
          ))}
        </div>
      </Card>

      {/* Simulation book KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Sim Policies', value: summary.active_policies.toLocaleString(), icon: TrendingUp, color: C.blue },
            { label: 'Sim GWP', value: fmt$(summary.gwp), icon: DollarSign, color: C.teal },
            { label: 'Sim Claims', value: summary.claims_count.toLocaleString(), icon: AlertTriangle, color: C.amber },
            { label: 'Claims Paid', value: fmt$(summary.claims_paid), icon: DollarSign, color: C.red },
            { label: 'Incurred LR', value: `${summary.loss_ratio_incurred}%`, icon: TrendingUp, color: summary.loss_ratio_incurred > 75 ? C.red : C.green },
          ].map(k => (
            <Card key={k.label} className="p-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
              <div className="flex items-center gap-2"><k.icon className="w-3.5 h-3.5" style={{ color: k.color }} />
                <span className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{k.label}</span></div>
              <p className="text-lg font-bold font-mono mt-1" style={{ color: C.navy }} data-testid={`sim-kpi-${k.label.toLowerCase().replace(/\s/g, '-')}`}>{k.value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Portfolio Growth */}
      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="portfolio-growth-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Rocket className="w-5 h-5" style={{ color: C.green }} />
            <div>
              <h3 className="text-sm font-bold" style={{ color: C.navy }}>Portfolio Growth</h3>
              <p className="text-xs" style={{ color: C.slate }}>Grow the sim book to a target GWP — every policy processed through the real flow: submission → AIRA scoring → MARAIS rating → bind. Balanced split across all classes.</p>
            </div>
          </div>
          {growth?.status !== 'running' && (
            <div className="flex items-center gap-2">
              <div className="flex rounded-sm overflow-hidden border" style={{ borderColor: 'rgba(136,150,168,0.3)' }} data-testid="growth-book-toggle">
                {['sim', 'live'].map(b => (
                  <button key={b} onClick={() => setGrowthBook(b)} data-testid={`growth-book-${b}`}
                    className="px-3 py-2 text-xs font-semibold"
                    style={growthBook === b ? { background: C.navy, color: '#fff' } : { background: '#fff', color: C.slate }}>
                    {b === 'sim' ? 'Sim Book' : 'Live Book'}
                  </button>
                ))}
              </div>
              <input type="number" min="0" step="100000" placeholder={`Target GWP (current ${fmt$(summary?.gwp || 0)})`}
                value={growthTarget} onChange={(e) => setGrowthTarget(e.target.value)}
                className="w-56 px-2 py-2 text-xs rounded-sm border focus:outline-none focus:ring-2 focus:ring-[#1A4C7C]"
                style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} data-testid="growth-target-input" />
              <button onClick={startGrowth} disabled={busy} data-testid="growth-start-button"
                className="flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs font-semibold text-white" style={{ background: C.green }}>
                <Rocket className="w-3.5 h-3.5" /> Grow Book
              </button>
              <button onClick={buildDemoBook} disabled={busy || demo?.status === 'running'} data-testid="demo-book-button"
                className="flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs font-semibold text-white" style={{ background: C.navy }}>
                <Sparkles className="w-3.5 h-3.5" /> Build Demo Book
              </button>
            </div>
          )}
        </div>
        {/* Presets */}
        {growth?.status !== 'running' && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="growth-presets">
            <span className="text-[10px] uppercase tracking-wider mr-1" style={{ color: C.slate }}>Presets</span>
            {presets.map(p => (
              <span key={p.preset_id} className="flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full text-[11px] font-medium cursor-pointer"
                style={{ background: 'rgba(26,76,124,0.08)', color: C.blue, border: '1px solid rgba(26,76,124,0.2)' }}
                onClick={() => applyPreset(p)} data-testid={`growth-preset-${p.preset_id}`}>
                {p.name}
                <span className="font-mono text-[10px]" style={{ color: C.slate }}>
                  {(p.book || 'sim').toUpperCase()}{p.target_gwp ? ` · ${fmt$(p.target_gwp)}` : ''}{p.broker_ids?.length ? ` · ${p.broker_ids.length} brk` : ''}
                </span>
                <button onClick={(e) => { e.stopPropagation(); deletePreset(p); }} data-testid={`growth-preset-delete-${p.preset_id}`}
                  className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ color: C.red }}>×</button>
              </span>
            ))}
            <button onClick={savePreset} data-testid="growth-preset-save"
              className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold" style={{ color: C.green, border: `1px dashed ${C.green}60` }}>
              + Save preset
            </button>
          </div>
        )}
        {/* Broker mix (live book only) */}
        {growthBook === 'live' && growth?.status !== 'running' && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid="broker-mix-section">
            <div className="flex items-center justify-between">
              <button onClick={() => setShowBrokers(v => !v)} data-testid="broker-mix-toggle"
                className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: C.blue }}>
                <Users className="w-3.5 h-3.5" />
                Broker Mix {selBrokers.length > 0 ? `— ${selBrokers.length} broker${selBrokers.length > 1 ? 's' : ''} targeted` : '— all brokers (random spread)'}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showBrokers ? 'rotate-180' : ''}`} />
              </button>
              {selBrokers.length > 0 && (
                <button onClick={() => setSelBrokers([])} className="text-[11px] underline" style={{ color: C.slate }} data-testid="broker-mix-clear">Clear selection</button>
              )}
            </div>
            {showBrokers && (
              <div className="mt-2 max-h-56 overflow-y-auto pr-1 space-y-2" data-testid="broker-mix-list">
                {Object.entries(brokers.reduce((acc, b) => { (acc[b.brokerage] = acc[b.brokerage] || []).push(b); return acc; }, {}))
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([firm, list]) => {
                    const ids = list.map(b => b.broker_id);
                    const selected = ids.filter(id => selBrokers.includes(id)).length;
                    return (
                      <div key={firm}>
                        <label className="flex items-center gap-2 py-0.5 text-xs font-semibold cursor-pointer" style={{ color: C.blue }}
                          data-testid={`brokerage-group-${firm.replace(/\s+/g, '-').toLowerCase()}`}>
                          <input type="checkbox" checked={selected === ids.length}
                            ref={el => { if (el) el.indeterminate = selected > 0 && selected < ids.length; }}
                            onChange={(e) => setSelBrokers(prev => e.target.checked
                              ? [...new Set([...prev, ...ids])]
                              : prev.filter(x => !ids.includes(x)))} />
                          {firm}
                          <span className="font-normal text-[10px]" style={{ color: C.slate }}>
                            {list.length} broker{list.length > 1 ? 's' : ''} · {fmt$(list.reduce((s, b) => s + b.gwp, 0))}{selected > 0 ? ` · ${selected} selected` : ''}
                          </span>
                        </label>
                        <div className="ml-5 grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-0.5">
                          {list.map(b => (
                            <label key={b.broker_id} className="flex items-center gap-2 py-0.5 text-xs cursor-pointer" style={{ color: C.navy }}>
                              <input type="checkbox" checked={selBrokers.includes(b.broker_id)}
                                onChange={(e) => setSelBrokers(prev => e.target.checked ? [...prev, b.broker_id] : prev.filter(x => x !== b.broker_id))}
                                data-testid={`broker-mix-check-${b.broker_id}`} />
                              <span className="truncate">{b.name}</span>
                              <span className="ml-auto font-mono text-[10px]" style={{ color: C.slate }}>{b.policies} pol · {fmt$(b.gwp)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
        {growth && growth.status !== 'none' && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid="growth-progress">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-semibold" style={{ color: growth.status === 'running' ? C.blue : growth.status === 'completed' ? C.green : growth.status === 'undone' ? C.slate : C.red }}>
                {growth.status === 'running' ? `GROWING ${(growth.book || 'sim').toUpperCase()} BOOK — processing policies through underwriting…` : growth.status === 'completed' ? `GROWTH COMPLETE (${(growth.book || 'sim').toUpperCase()} BOOK)` : growth.status === 'undone' ? `GROWTH UNDONE (${(growth.book || 'sim').toUpperCase()} BOOK)` : 'GROWTH FAILED'}
                {growth.error ? ` — ${growth.error}` : ''}
              </span>
              <span className="font-mono" style={{ color: C.navy }} data-testid="growth-gwp-progress">
                {fmt$(growth.current_gwp || 0)} / {fmt$(growth.target_gwp || 0)}
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.15)' }}>
              <div className="h-full rounded-full transition-all duration-500" style={{
                width: `${Math.min(100, ((growth.current_gwp || 0) / (growth.target_gwp || 1)) * 100)}%`,
                background: growth.status === 'failed' ? C.red : C.green }} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs" style={{ color: C.slate }}>
              <span>Bound: <b className="font-mono" style={{ color: C.navy }}>{(growth.bound || 0).toLocaleString()}</b></span>
              <span>Submissions: <b className="font-mono" style={{ color: C.navy }}>{(growth.submissions || 0).toLocaleString()}</b></span>
              <span>Approved: <b className="font-mono" style={{ color: C.green }}>{growth.decisions?.APPROVE || 0}</b></span>
              <span>Conditions: <b className="font-mono" style={{ color: C.teal }}>{growth.decisions?.CONDITIONS || 0}</b></span>
              <span>Referred: <b className="font-mono" style={{ color: C.amber }}>{growth.decisions?.REFER || 0}</b></span>
              <span>Declined: <b className="font-mono" style={{ color: C.red }}>{growth.decisions?.DECLINE || 0}</b></span>
            </div>
            {growth.status === 'completed' && growth.by_class && Object.keys(growth.by_class).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="growth-class-split">
                {Object.entries(growth.by_class).sort((a, b) => b[1].gwp - a[1].gwp).map(([cls, v]) => (
                  <span key={cls} className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: '#F5F7FA', color: C.navy, border: '1px solid rgba(136,150,168,0.2)' }}>
                    {cls}: {fmt$(v.gwp)} ({v.bound})
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Demo book pipeline progress */}
        {demo && demo.status !== 'none' && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid="demo-book-progress">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-semibold" style={{ color: demo.status === 'running' ? C.blue : demo.status === 'completed' ? C.green : C.red }}>
                {demo.status === 'running' ? 'BUILDING DEMO BOOK…' : demo.status === 'completed' ? 'DEMO BOOK COMPLETE' : 'DEMO BOOK FAILED'}
              </span>
              <span className="font-mono text-[10px]" style={{ color: C.slate }}>
                {(demo.params?.book || 'sim').toUpperCase()} · target {fmt$(demo.params?.target_gwp || 0)}
              </span>
            </div>
            <div className="flex items-center gap-1.5" data-testid="demo-book-stages">
              {[['grow', 'Grow Book'], ['seed', 'Seed Claims'], ['prepare', 'Prepare Offers'], ['drive', 'Drive Responses']].map(([k, label], i) => {
                const st = demo.stages?.[k];
                const isCurrent = demo.status === 'running' && demo.current_stage === k;
                const done = st && !st.error;
                const failed = st?.error;
                return (
                  <React.Fragment key={k}>
                    {i > 0 && <div className="h-px flex-1" style={{ background: 'rgba(136,150,168,0.25)' }} />}
                    <span className="px-2 py-1 rounded-full text-[10px] font-semibold flex items-center gap-1" data-testid={`demo-stage-${k}`}
                      style={{ background: failed ? 'rgba(226,75,74,0.1)' : done ? 'rgba(29,158,117,0.12)' : isCurrent ? 'rgba(26,76,124,0.1)' : '#F5F7FA',
                               color: failed ? C.red : done ? C.green : isCurrent ? C.blue : C.slate }}>
                      {done ? <CheckCircle className="w-3 h-3" /> : isCurrent ? <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: C.blue }} /> : null}
                      {label}
                      {st && !failed && (
                        <span className="font-mono font-normal">
                          {k === 'grow' ? `${st.bound} bound` : k === 'seed' ? `${st.claims} claims · ${st.lr}% LR` : k === 'prepare' ? `${st.prepared} offers` : `${st.accepted}✓ ${st.declined}✗`}
                        </span>
                      )}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>
            {demo.status === 'completed' && (
              demo.script ? (
                <div className="mt-3 p-3 rounded-md" style={{ background: '#F8FAFC', border: '1px solid rgba(26,76,124,0.15)' }} data-testid="demo-script-card">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <ScrollText className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.navy }} />
                      <p className="text-xs font-bold" style={{ color: C.navy }} data-testid="demo-script-headline">{demo.script.headline}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={copyScript} data-testid="demo-script-copy"
                        className="flex items-center gap-1 px-2.5 py-1 rounded-sm text-[10px] font-semibold"
                        style={{ color: C.blue, border: `1px solid ${C.blue}40` }}>
                        <Copy className="w-3 h-3" /> Copy script
                      </button>
                      <button onClick={async () => {
                        try {
                          await api.post('/simulation/grow/demo-book/script/email');
                          toast.success('Demo script emailed — check your inbox');
                        } catch (err) { toast.error(err.response?.data?.detail || 'Email failed'); }
                      }} data-testid="demo-script-email"
                        className="flex items-center gap-1 px-2.5 py-1 rounded-sm text-[10px] font-semibold"
                        style={{ color: C.green, border: `1px solid ${C.green}40` }}>
                        <Mail className="w-3 h-3" /> Email script
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div data-testid="demo-script-points">
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.slate }}>Talking Points</p>
                      <ol className="space-y-1">
                        {demo.script.talking_points.map((t, i) => (
                          <li key={i} className="flex gap-2 text-xs" style={{ color: '#3A4A5A' }}>
                            <span className="font-mono font-bold shrink-0" style={{ color: C.green }}>{i + 1}.</span>{t}
                          </li>
                        ))}
                      </ol>
                    </div>
                    {demo.script.demo_flow?.length > 0 && (
                      <div data-testid="demo-script-flow">
                        <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.slate }}>Demo Flow</p>
                        <ol className="space-y-1">
                          {demo.script.demo_flow.map((t, i) => (
                            <li key={i} className="flex gap-2 text-xs" style={{ color: '#3A4A5A' }}>
                              <span className="font-mono font-bold shrink-0" style={{ color: C.blue }}>{i + 1}.</span>{t}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <button onClick={generateScript} disabled={busy} data-testid="demo-script-generate"
                  className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-semibold"
                  style={{ color: C.navy, border: `1px solid ${C.navy}40` }}>
                  <ScrollText className="w-3 h-3" /> Generate Demo Script
                </button>
              )
            )}
          </div>
        )}
        {/* Claims history seeding */}
        {growth?.status !== 'running' && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid="claims-seed-section">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4" style={{ color: C.amber }} />
                <div>
                  <p className="text-xs font-semibold" style={{ color: C.navy }}>Claims History Seeding</p>
                  <p className="text-[11px]" style={{ color: C.slate }}>Backdates grown policies 3-11 months and lodges a realistic claims history through FNOL — loss ratios look mature from day one.</p>
                </div>
              </div>
              {seed?.status !== 'running' && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <input type="number" min="30" max="65" value={seedLr} onChange={(e) => setSeedLr(e.target.value)}
                      className="w-16 px-2 py-2 text-xs rounded-sm border focus:outline-none focus:ring-2 focus:ring-[#1A4C7C]"
                      style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} data-testid="seed-lr-input" />
                    <span className="text-xs" style={{ color: C.slate }}>% target LR</span>
                  </div>
                  <button onClick={startSeed} disabled={busy} data-testid="seed-claims-button"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs font-semibold text-white" style={{ background: C.amber }}>
                    <History className="w-3.5 h-3.5" /> Seed Claims History
                  </button>
                </div>
              )}
            </div>
            {seed && seed.status !== 'none' && (
              <div className="mt-2" data-testid="seed-progress">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-semibold" style={{ color: seed.status === 'running' ? C.blue : seed.status === 'completed' ? C.green : seed.status === 'undone' ? C.slate : C.red }}>
                    {seed.status === 'running' ? 'SEEDING — lodging backdated claims via FNOL…' : seed.status === 'completed' ? 'CLAIMS HISTORY SEEDED' : seed.status === 'undone' ? 'SEED UNDONE' : 'SEED FAILED'}
                    {seed.error ? ` — ${seed.error}` : ''}
                  </span>
                  <span className="font-mono" style={{ color: C.navy }} data-testid="seed-lr-result">
                    {seed.achieved_lr ? `${seed.achieved_lr}% LR achieved` : ''}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs" style={{ color: C.slate }}>
                  <span>Policies matured: <b className="font-mono" style={{ color: C.navy }}>{(seed.policies_matured || 0).toLocaleString()}</b></span>
                  <span>Claims lodged: <b className="font-mono" style={{ color: C.navy }}>{(seed.claims_created || 0).toLocaleString()}</b></span>
                  <span>Auto-settled: <b className="font-mono" style={{ color: C.green }}>{(seed.auto_settled || 0).toLocaleString()}</b></span>
                  <span>Incurred: <b className="font-mono" style={{ color: C.amber }}>{fmt$(seed.incurred_total || 0)}</b></span>
                  <span>Earned: <b className="font-mono" style={{ color: C.navy }}>{fmt$(seed.earned_total || 0)}</b></span>
                </div>
                {seed.status === 'completed' && seed.by_class && Object.keys(seed.by_class).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="seed-class-split">
                    {Object.entries(seed.by_class).sort((a, b) => b[1].incurred - a[1].incurred).map(([cls, v]) => (
                      <span key={cls} className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: '#FDF6EC', color: C.navy, border: '1px solid rgba(232,160,32,0.3)' }}>
                        {cls}: {v.achieved_lr_pct}% LR ({v.claims} claims)
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Renewal wave preview */}
        {wave && wave.total > 0 && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid="renewal-wave-section">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4" style={{ color: C.blue }} />
                <p className="text-xs font-semibold" style={{ color: C.navy }}>Renewal Wave — Next {wave.days} Days</p>
                <span className="text-[11px]" style={{ color: C.slate }}>
                  {wave.total.toLocaleString()} grown policies fall due · {fmt$(wave.total_premium)} premium at stake · <b data-testid="wave-offers-ready" style={{ color: (wave.offers_ready || 0) >= wave.total ? C.green : C.amber }}>{wave.offers_ready || 0}/{wave.total} offers ready</b>
                </span>
              </div>
              {(wave.offers_ready || 0) < wave.total && (
                <button onClick={prepareWave} disabled={busy} data-testid="wave-prepare-button"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white" style={{ background: C.blue }}>
                  <CalendarClock className="w-3 h-3" /> Prepare Offers (ARIA)
                </button>
              )}
              {(wave.undecided || 0) > 0 && (
                <div className="flex items-center gap-1.5">
                  <input type="number" min="10" max="100" value={driveRate} onChange={(e) => setDriveRate(e.target.value)}
                    className="w-14 px-1.5 py-1.5 text-[11px] rounded-sm border focus:outline-none"
                    style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} data-testid="wave-drive-rate-input" />
                  <span className="text-[10px]" style={{ color: C.slate }}>% accept</span>
                  <button onClick={driveWave} disabled={busy} data-testid="wave-drive-button"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white" style={{ background: C.green }}>
                    <Users className="w-3 h-3" /> Drive Responses (ARIA)
                  </button>
                </div>
              )}
            </div>
            {/* funnel chips */}
            {wave.funnel && Object.keys(wave.funnel).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="wave-funnel">
                {[['quote_generated', 'Quoted', C.blue], ['offered', 'Offered', C.teal], ['accepted', 'Accepted', C.green], ['declined', 'Declined', C.red], ['held_for_review', 'Held', C.amber]].map(([k, label, color]) =>
                  wave.funnel[k] ? (
                    <span key={k} className="px-2 py-0.5 rounded-full text-[10px] font-semibold" data-testid={`wave-funnel-${k}`}
                      style={{ background: `${color}18`, color }}>
                      {label}: {wave.funnel[k]}
                    </span>
                  ) : null)}
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 mb-2">
              {Object.entries(wave.buckets).map(([k, v]) => (
                <div key={k} className="p-2 rounded-sm" style={{ background: '#F5F7FA' }} data-testid={`renewal-wave-bucket-${k}`}>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>Due in {k} days</p>
                  <p className="text-sm font-bold font-mono" style={{ color: C.navy }}>{v.count.toLocaleString()} <span className="font-normal text-xs" style={{ color: C.slate }}>· {fmt$(v.premium)}</span></p>
                </div>
              ))}
            </div>
            {wave.soonest.length > 0 && (
              <table className="w-full text-xs" data-testid="renewal-wave-table">
                <thead><tr className="border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                  {['Due', 'Business', 'Class', 'Broker', 'Book', 'Premium', 'Offer'].map(h =>
                    <th key={h} className="text-left py-1 pr-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {wave.soonest.slice(0, 8).map(p => (
                    <tr key={p.policy_id} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                      <td className="py-1 pr-3 font-mono" style={{ color: p.days_to_expiry <= 30 ? C.red : C.navy }}>{p.days_to_expiry}d</td>
                      <td className="py-1 pr-3 truncate max-w-[220px]" style={{ color: C.navy }}>{p.business_name}</td>
                      <td className="py-1 pr-3" style={{ color: C.slate }}>{p.class}</td>
                      <td className="py-1 pr-3" style={{ color: C.slate }}>{p.broker_name}</td>
                      <td className="py-1 pr-3 font-mono uppercase text-[10px]" style={{ color: p.book === 'sim' ? C.teal : C.green }}>{p.book}</td>
                      <td className="py-1 pr-3 font-mono" style={{ color: C.navy }}>{fmt$(p.annual_premium)}</td>
                      <td className="py-1 pr-3">
                        {p.renewal_status
                          ? <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{
                              background: p.renewal_status === 'accepted' ? 'rgba(29,158,117,0.12)' : p.renewal_status === 'declined' ? 'rgba(226,75,74,0.1)' : p.renewal_status === 'held_for_review' ? 'rgba(232,160,32,0.12)' : 'rgba(0,180,216,0.12)',
                              color: p.renewal_status === 'accepted' ? C.green : p.renewal_status === 'declined' ? C.red : p.renewal_status === 'held_for_review' ? C.amber : C.teal }}>
                              {p.renewal_status === 'accepted' ? 'Accepted' : p.renewal_status === 'declined' ? 'Declined' : p.renewal_status === 'held_for_review' ? 'Held' : p.renewal_status === 'offered' ? 'Offered' : 'Ready'}
                            </span>
                          : <span className="text-[10px]" style={{ color: C.slate }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {/* Growth run history */}
        {history.length > 0 && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid="growth-history">
            <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: C.slate }}>Growth Run History</p>
            <table className="w-full text-xs">
              <thead><tr className="border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                {['When', 'Type', 'Book', 'Result', 'Volume', 'Status', ''].map((h, i) =>
                  <th key={i} className="text-left py-1 pr-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {history.map(r => (
                  <tr key={`${r.type}-${r.run_id}`} data-testid={`growth-history-row-${r.run_id}`} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                    <td className="py-1 pr-3 font-mono" style={{ color: C.navy }}>{new Date(r.started_at).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="py-1 pr-3">
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={r.type === 'growth' ? { background: 'rgba(29,158,117,0.12)', color: C.green } : { background: 'rgba(232,160,32,0.12)', color: C.amber }}>
                        {r.type === 'growth' ? 'GWP Growth' : 'Claims Seed'}
                      </span>
                    </td>
                    <td className="py-1 pr-3 font-mono uppercase" style={{ color: C.slate }}>{r.type === 'growth' ? (r.book || 'sim') : '—'}</td>
                    <td className="py-1 pr-3 font-mono" style={{ color: C.navy }}>
                      {r.type === 'growth' ? `${fmt$(r.start_gwp || 0)} → ${fmt$(r.current_gwp || 0)}` : `${r.achieved_lr || 0}% LR (target ${Math.round((r.target_lr || 0) * 100)}%)`}
                    </td>
                    <td className="py-1 pr-3 font-mono" style={{ color: C.navy }}>
                      {r.type === 'growth' ? `${(r.bound || 0).toLocaleString()} bound / ${(r.submissions || 0).toLocaleString()} subs` : `${(r.claims_created || 0).toLocaleString()} claims / ${(r.policies_matured || 0).toLocaleString()} policies`}
                    </td>
                    <td className="py-1 pr-3">
                      <span className="font-semibold text-[10px]" style={{ color: r.status === 'completed' ? C.green : r.status === 'running' ? C.blue : r.status === 'undone' ? C.slate : C.red }}>{(r.status || '').toUpperCase()}</span>
                    </td>
                    <td className="py-1">
                      {r.status === 'completed' && (
                        <button onClick={() => undoRun(r)} disabled={busy} data-testid={`growth-undo-${r.run_id}`}
                          className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-semibold"
                          style={{ color: C.red, border: `1px solid ${C.red}40` }}>
                          <RotateCcw className="w-3 h-3" /> Undo
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Brokerage scorecards */}
      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="firm-scorecards-card">
        <button onClick={() => {
          setShowFirms(v => !v);
          if (firms.length === 0) api.get('/simulation/grow/firm-scorecards').then(({ data }) => setFirms(data)).catch(() => {});
        }} className="flex items-center gap-2 w-full" data-testid="firm-scorecards-toggle">
          <Building2 className="w-4 h-4" style={{ color: C.blue }} />
          <span className="text-xs font-bold" style={{ color: C.navy }}>Brokerage Scorecards</span>
          <span className="text-[11px]" style={{ color: C.slate }}>book size & growth contribution per firm</span>
          <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${showFirms ? 'rotate-180' : ''}`} style={{ color: C.slate }} />
        </button>
        {showFirms && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="firm-scorecards-grid">
            {firms.length === 0 && <p className="text-xs col-span-3" style={{ color: C.slate }}>Loading scorecards…</p>}
            {firms.map(f => (
              <div key={f.brokerage_id} className="p-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid={`firm-card-${f.brokerage_id}`}>
                <p className="text-xs font-bold truncate" style={{ color: C.navy }}>{f.name}</p>
                <p className="text-[10px]" style={{ color: C.slate }}>{f.city}, {f.state} · {f.specialty} · {f.brokers} brokers</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[9px] uppercase tracking-wider" style={{ color: C.slate }}>Book GWP</p>
                    <p className="text-sm font-bold font-mono" style={{ color: C.navy }}>{fmt$(f.gwp)}</p>
                    <p className="text-[10px]" style={{ color: C.slate }}>{f.policies.toLocaleString()} policies</p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase tracking-wider" style={{ color: C.slate }}>Growth Contribution</p>
                    <p className="text-sm font-bold font-mono" style={{ color: f.growth_gwp > 0 ? C.green : C.slate }}>{f.growth_gwp > 0 ? fmt$(f.growth_gwp) : '—'}</p>
                    <p className="text-[10px]" style={{ color: C.slate }}>{f.growth_policies > 0 ? `${f.growth_policies} grown · ${f.growth_share_pct}% of book` : 'no grown policies'}</p>
                  </div>
                </div>
                {f.top_class && <p className="mt-1.5 text-[10px]" style={{ color: C.slate }}>Top class: <span className="font-medium" style={{ color: C.navy }}>{f.top_class}</span></p>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Retention insights */}
      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="retention-insights-card">
        <button onClick={() => {
          setShowRetention(v => !v);
          if (!retention) api.get('/simulation/grow/retention-insights').then(({ data }) => setRetention(data)).catch(() => {});
        }} className="flex items-center gap-2 w-full" data-testid="retention-insights-toggle">
          <TrendingUp className="w-4 h-4" style={{ color: C.green }} />
          <span className="text-xs font-bold" style={{ color: C.navy }}>Retention Insights</span>
          <span className="text-[11px]" style={{ color: C.slate }}>driven accept rates & renewed GWP by class and firm</span>
          <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${showRetention ? 'rotate-180' : ''}`} style={{ color: C.slate }} />
        </button>
        {showRetention && (
          !retention ? <p className="mt-3 text-xs" style={{ color: C.slate }}>Loading…</p> :
          retention.totals.responses === 0 ? <p className="mt-3 text-xs" style={{ color: C.slate }}>No driven responses yet — prepare offers and run Drive Responses (ARIA) first.</p> : (
          <div className="mt-3" data-testid="retention-insights-body">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs mb-3" data-testid="retention-totals">
              <span style={{ color: C.slate }}>Responses: <b className="font-mono" style={{ color: C.navy }}>{retention.totals.responses}</b></span>
              <span style={{ color: C.slate }}>Accept rate: <b className="font-mono" style={{ color: C.green }}>{retention.totals.accept_rate_pct}%</b></span>
              <span style={{ color: C.slate }}>Renewed GWP: <b className="font-mono" style={{ color: C.navy }}>{fmt$(retention.totals.renewed_gwp)}</b></span>
              <span style={{ color: C.slate }}>Declined: <b className="font-mono" style={{ color: C.red }}>{retention.totals.declined}</b></span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {[['By Class', retention.by_class, 'retention-by-class'], ['By Firm', retention.by_firm, 'retention-by-firm']].map(([title, rows, tid]) => (
                <div key={tid} data-testid={tid}>
                  <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: C.slate }}>{title}</p>
                  <div className="space-y-1.5">
                    {rows.slice(0, 8).map(r => (
                      <div key={r.name} className="flex items-center gap-2 text-xs">
                        <span className="w-44 truncate" style={{ color: C.navy }}>{r.name}</span>
                        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(226,75,74,0.15)' }}>
                          <div className="h-full rounded-full" style={{ width: `${r.accept_rate_pct}%`, background: C.green }} />
                        </div>
                        <span className="w-12 text-right font-mono" style={{ color: C.green }}>{r.accept_rate_pct}%</span>
                        <span className="w-20 text-right font-mono" style={{ color: C.navy }}>{fmt$(r.renewed_gwp)}</span>
                        <span className="w-14 text-right font-mono text-[10px]" style={{ color: C.slate }}>{r.accepted}✓ {r.declined}✗</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </Card>

      {/* Insights trend — watch the agents learn */}
      {runs.length >= 2 && (
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="sim-trend-chart">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Simulation Insights — Book Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={[...runs].reverse().map(r => ({
              date: (r.date || '').slice(5),
              gwp: Math.round((r.portfolio?.summary?.gwp || 0) / 1000),
              lossRatio: r.portfolio?.summary?.loss_ratio_incurred || 0,
              calibrations: r.portfolio?.calibrations?.length || 0,
            }))} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(136,150,168,0.15)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.slate }} />
              <YAxis yAxisId="gwp" tick={{ fontSize: 10, fill: C.slate }} tickFormatter={v => `$${v}K`} />
              <YAxis yAxisId="lr" orientation="right" tick={{ fontSize: 10, fill: C.slate }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={(v, n) => n === 'Sim GWP ($K)' ? [`$${v}K`, n] : n === 'Loss Ratio (%)' ? [`${v}%`, n] : [v, n]}
                contentStyle={{ fontSize: 11, borderRadius: 4 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area yAxisId="gwp" type="monotone" dataKey="gwp" name="Sim GWP ($K)" fill="rgba(0,180,216,0.15)" stroke={C.teal} strokeWidth={2} />
              <Line yAxisId="lr" type="monotone" dataKey="lossRatio" name="Incurred LR (%)" stroke={C.red} strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="lr" type="monotone" dataKey="calibrations" name="Calibrations" stroke={C.green} strokeWidth={1.5} strokeDasharray="4 3" dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Latest run report */}
      {latest && (
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="sim-latest-run">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>
            Latest Run — {latest.date} <span className="ml-2 font-normal normal-case" style={{ color: C.slate }}>({latest.duration_sec}s · est ${latest.llm_spend_est} of ${latest.budget_usd} budget)</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
            <div><p style={{ color: C.slate }}>NB Quotes</p><p className="font-mono font-bold" style={{ color: C.navy }}>{latest.nb ? Object.values(latest.nb.decisions).reduce((a, b) => a + b, 0) : 0} scored · {latest.nb?.bound || 0} bound</p></div>
            <div><p style={{ color: C.slate }}>Renewals</p><p className="font-mono font-bold" style={{ color: C.navy }}>{latest.renewals?.accepted || 0} accepted / {latest.renewals?.generated || 0}</p></div>
            <div><p style={{ color: C.slate }}>Claims</p><p className="font-mono font-bold" style={{ color: C.navy }}>{latest.claims?.created || 0} lodged · {latest.claims?.auto_settled || 0} auto-settled</p></div>
            <div><p style={{ color: C.slate }}>Endorsements</p><p className="font-mono font-bold" style={{ color: C.navy }}>{latest.endorsements?.processed || 0}</p></div>
            <div><p style={{ color: C.slate }}>Cancellations</p><p className="font-mono font-bold" style={{ color: C.navy }}>{latest.cancellations?.processed || 0}</p></div>
          </div>
          {latest.portfolio?.findings?.length > 0 && (
            <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
              <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: C.slate }}>Risk Management Findings</p>
              {latest.portfolio.findings.map((f, i) => (
                <p key={i} className="text-xs py-0.5 flex items-center gap-1.5" style={{ color: C.navy }}>
                  <AlertTriangle className="w-3 h-3 shrink-0" style={{ color: C.amber }} />{f}</p>
              ))}
              {latest.portfolio.calibrations?.length > 0 && (
                <p className="text-xs mt-1.5 flex items-center gap-1.5" style={{ color: C.green }}>
                  <CheckCircle className="w-3 h-3" />Auto-calibration applied: {latest.portfolio.calibrations.map(c => `${c.class} (${c.action})`).join(', ')}</p>
              )}
            </div>
          )}
          {latest.portfolio?.commentary && (
            <div className="mt-3 p-3 rounded-sm text-xs leading-relaxed" style={{ background: '#F5F7FA', color: C.navy }} data-testid="sim-commentary">
              <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.slate }}>AI Risk Manager's Daily Report</p>
              {latest.portfolio.commentary}
            </div>
          )}
        </Card>
      )}

      {/* Run history */}
      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.navy }}>Run History</h3>
        {runs.length === 0 ? (
          <p className="text-xs py-4 text-center" style={{ color: C.slate }}>No runs yet. Enable the engine or click Run Now.</p>
        ) : (
          <table className="w-full text-xs">
            <thead><tr className="border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
              {['Date', 'NB Bound', 'Renewals', 'Claims', 'Endorse', 'Cancel', 'Spend', 'Duration'].map(h =>
                <th key={h} className="text-left py-1.5 pr-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.run_id} data-testid={`sim-run-row-${r.run_id}`} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                  <td className="py-1.5 pr-3 font-mono" style={{ color: C.navy }}>{r.date}</td>
                  <td className="py-1.5 pr-3 font-mono" style={{ color: C.navy }}>{r.nb?.bound ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-mono" style={{ color: C.navy }}>{r.renewals?.accepted ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-mono" style={{ color: C.navy }}>{r.claims?.created ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-mono" style={{ color: C.navy }}>{r.endorsements?.processed ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-mono" style={{ color: C.navy }}>{r.cancellations?.processed ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-mono" style={{ color: C.teal }}>${r.llm_spend_est}</td>
                  <td className="py-1.5 pr-3 font-mono" style={{ color: C.slate }}>{r.duration_sec}s</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
